/**
 * Primitivas de UI de Gourmet Urbano.
 *
 * El design system vivía en `docs/06` y no en el código: cada pantalla
 * llevaba sus propios `style={{}}`, así que una corrección de espaciado
 * había que repetirla seis veces y ninguna quedaba igual. Esto lo
 * centraliza — cambiar un botón aquí cambia la app entera.
 *
 * Reglas de `docs/06` que estos componentes hacen cumplir por construcción:
 *
 * - **§1.1** El cromo es cream y neutro; el color de marca se reserva para
 *   la acción y para señalar lo que es del usuario.
 * - **§1.2** Una sola acción primaria por pantalla. Por eso el `Boton`
 *   secundario es texto, no otro bloque de color que compita.
 * - **§2** 44px de área táctil como mínimo absoluto.
 * - **§4** Jerarquía tipográfica fija; nada de texto por debajo de 14px.
 * - **§2.1** Ningún color en crudo: todo pasa por tokens semánticos, que
 *   es lo que hace viable el modo oscuro sin reescribir la app.
 *
 * Estilos en línea y no CSS modules por coherencia con lo que ya existía:
 * el proyecto no tiene pipeline de CSS por componente, y montarlo ahora
 * sería una decisión aparte. Lo que importa es que los valores salgan de
 * los tokens, y eso se cumple igual.
 */

import type { CSSProperties, ReactNode } from 'react';

/* ─────────────────────────────── Botón ─────────────────────────────── */

export interface BotonProps {
  children: ReactNode;
  onClick?: (() => void) | undefined;
  variante?: 'primario' | 'secundario' | 'peligro';
  tipo?: 'button' | 'submit';
  disabled?: boolean | undefined;
  /** Ocupa todo el ancho. Es lo normal en móvil; por eso el default. */
  ancho?: boolean;
}

export function Boton({
  children,
  onClick,
  variante = 'primario',
  tipo = 'button',
  disabled,
  ancho = true,
}: BotonProps) {
  const base: CSSProperties = {
    minHeight: 'var(--gu-touch-comfortable)',
    padding: '0 var(--gu-space-5)',
    borderRadius: 'var(--gu-radius-md)',
    fontSize: 'var(--gu-text-body)',
    fontFamily: 'inherit',
    fontWeight: 550,
    cursor: disabled ? 'not-allowed' : 'pointer',
    width: ancho ? '100%' : 'auto',
    transition: 'opacity var(--gu-duration-fast) var(--gu-ease-standard)',
    opacity: disabled ? 0.55 : 1,
  };

  const porVariante: Record<string, CSSProperties> = {
    /*
     * `--gu-primary-strong` y NO `--gu-primary`: el color de marca con
     * blanco encima da 3.46:1 y falla AA. La variante fuerte da 5.19:1
     * (ADR-014). Es un error que no se ve mirando.
     */
    primario: {
      background: 'var(--gu-primary-strong)',
      color: 'var(--gu-text-on-brand)',
      border: 'none',
    },
    /*
     * Texto sobre nada. `docs/06` §1.2 exige una sola acción primaria
     * visible: un secundario con su propio bloque de color competiría.
     */
    secundario: {
      background: 'none',
      color: 'var(--gu-text-link)',
      border: 'none',
    },
    peligro: {
      background: 'none',
      color: 'var(--gu-danger)',
      border: '1px solid var(--gu-danger)',
    },
  };

  return (
    <button
      type={tipo}
      onClick={onClick}
      disabled={disabled}
      style={{ ...base, ...porVariante[variante] }}
    >
      {children}
    </button>
  );
}

/* ─────────────────────────────── Campo ─────────────────────────────── */

export interface CampoProps {
  id: string;
  etiqueta: string;
  valor: string;
  onChange: (v: string) => void;
  tipo?: string;
  /** Texto de apoyo bajo el campo. No es un error. */
  ayuda?: string | undefined;
  error?: string | undefined;
  autoFocus?: boolean | undefined;
  autoComplete?: string | undefined;
  inputMode?: 'text' | 'email' | 'numeric' | 'tel';
  maxLength?: number | undefined;
  /** Para el OTP: dígitos separados y grandes. */
  destacado?: boolean;
}

export function Campo({
  id,
  etiqueta,
  valor,
  onChange,
  tipo = 'text',
  ayuda,
  error,
  autoFocus,
  autoComplete,
  inputMode,
  maxLength,
  destacado,
}: CampoProps) {
  const idAyuda = ayuda ? `${id}-ayuda` : undefined;
  const idError = error ? `${id}-error` : undefined;
  const describedBy = [idError, idAyuda].filter(Boolean).join(' ') || undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gu-space-2)' }}>
      <label
        htmlFor={id}
        style={{
          fontSize: 'var(--gu-text-body-sm)',
          color: 'var(--gu-text-secondary)',
          fontWeight: 550,
        }}
      >
        {etiqueta}
      </label>

      <input
        id={id}
        type={tipo}
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        inputMode={inputMode}
        maxLength={maxLength}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        style={{
          minHeight: 'var(--gu-touch-comfortable)',
          padding: '0 var(--gu-space-4)',
          borderRadius: 'var(--gu-radius-md)',
          border: `1px solid ${error ? 'var(--gu-danger)' : 'var(--gu-border-default)'}`,
          background: 'var(--gu-surface-raised)',
          color: 'var(--gu-text-primary)',
          fontFamily: 'inherit',
          /*
           * 16px exactos, nunca menos: por debajo, Safari en iOS hace zoom
           * al enfocar y descoloca la pantalla. No es estética.
           */
          fontSize: destacado ? '1.5rem' : '1rem',
          letterSpacing: destacado ? '0.3em' : 'normal',
          textAlign: destacado ? 'center' : 'left',
          width: '100%',
          boxSizing: 'border-box',
        }}
      />

      {error && (
        <p
          id={idError}
          role="alert"
          style={{
            margin: 0,
            fontSize: 'var(--gu-text-body-sm)',
            color: 'var(--gu-text-primary)',
          }}
        >
          {error}
        </p>
      )}
      {ayuda && !error && (
        <p
          id={idAyuda}
          style={{
            margin: 0,
            fontSize: 'var(--gu-text-body-sm)',
            color: 'var(--gu-text-secondary)',
          }}
        >
          {ayuda}
        </p>
      )}
    </div>
  );
}

/* ─────────────────────────── Tipografía ─────────────────────────── */

export interface TituloProps {
  children: ReactNode;
  /** `display` solo en estados vacíos y rituales: máx. 1 por pantalla. */
  variante?: 'display' | 'h1' | 'h2' | 'h3';
  nivel?: 1 | 2 | 3;
  centrado?: boolean;
}

export function Titulo({ children, variante = 'h1', nivel, centrado }: TituloProps) {
  const tamanos = {
    display: 'var(--gu-text-display)',
    h1: 'var(--gu-text-h1)',
    h2: 'var(--gu-text-h2)',
    h3: 'var(--gu-text-h3)',
  };
  // `display` es siempre h1: es el título de la pantalla aunque se vea
  // distinto. El nivel se puede forzar cuando la jerarquía lo pide.
  const H = (`h${nivel ?? (variante === 'display' ? 1 : variante === 'h1' ? 1 : variante === 'h2' ? 2 : 3)}`) as 'h1';

  return (
    <H
      style={{
        fontSize: tamanos[variante],
        lineHeight: 'var(--gu-leading-tight)',
        fontWeight: variante === 'display' ? 700 : 600,
        color: 'var(--gu-text-primary)',
        margin: 0,
        textAlign: centrado ? 'center' : 'left',
        letterSpacing: variante === 'display' ? '-0.02em' : 'normal',
      }}
    >
      {children}
    </H>
  );
}

export interface TextoProps {
  children: ReactNode;
  /** `sagrado` respira más: es la voz de una persona (`docs/06` §4). */
  variante?: 'normal' | 'secundario' | 'sagrado' | 'caption';
  centrado?: boolean;
}

export function Texto({ children, variante = 'normal', centrado }: TextoProps) {
  const estilos: Record<string, CSSProperties> = {
    normal: { fontSize: 'var(--gu-text-body)', color: 'var(--gu-text-primary)' },
    secundario: { fontSize: 'var(--gu-text-body)', color: 'var(--gu-text-secondary)' },
    sagrado: {
      fontSize: 'var(--gu-text-body)',
      color: 'var(--gu-text-primary)',
      lineHeight: 'var(--gu-leading-relaxed)',
    },
    /* 12px: SOLO metadatos. Nunca contenido esencial (`docs/06` §4). */
    caption: { fontSize: 'var(--gu-text-caption)', color: 'var(--gu-text-secondary)' },
  };

  return (
    <p
      style={{
        margin: 0,
        lineHeight: 'var(--gu-leading-normal)',
        textAlign: centrado ? 'center' : 'left',
        ...estilos[variante],
      }}
    >
      {children}
    </p>
  );
}

/* ───────────────────────── Contenedores ───────────────────────── */

export interface PantallaProps {
  children: ReactNode;
  /** Centra verticalmente. Para pantallas de un solo bloque. */
  centrada?: boolean;
}

/**
 * Espacio reservado bajo la barra de navegación, que es `position: fixed`
 * y por tanto no ocupa flujo. Sin esto, el último botón de cada pantalla
 * queda debajo de la barra y no se puede tocar. Es el alto de la barra
 * (3.5rem) más la zona segura del dispositivo, más aire.
 */
export const ESPACIO_NAV = 'calc(3.5rem + var(--gu-safe-bottom) + var(--gu-space-6))';

export function Pantalla({ children, centrada }: PantallaProps) {
  return (
    <main
      style={{
        minHeight: '100dvh',
        background: 'var(--gu-surface-app)',
        padding: `var(--gu-space-6) var(--gu-gutter) ${ESPACIO_NAV}`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: centrada ? 'center' : 'flex-start',
        gap: 'var(--gu-space-5)',
        /* Una línea de 100 caracteres no se lee. En móvil no aplica, pero
           en escritorio sin esto el texto cruza la pantalla entera. */
        maxWidth: '32rem',
        marginInline: 'auto',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {children}
    </main>
  );
}

export interface TarjetaProps {
  children: ReactNode;
  onClick?: (() => void) | undefined;
}

export function Tarjeta({ children, onClick }: TarjetaProps) {
  const estilo: CSSProperties = {
    background: 'var(--gu-surface-raised)',
    borderRadius: 'var(--gu-radius-lg)',
    padding: 'var(--gu-space-4)',
    border: '1px solid var(--gu-border-subtle)',
    width: '100%',
    boxSizing: 'border-box',
  };

  if (!onClick) return <div style={estilo}>{children}</div>;

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...estilo,
        textAlign: 'left',
        font: 'inherit',
        color: 'inherit',
        cursor: 'pointer',
        minHeight: 'var(--gu-touch-min)',
      }}
    >
      {children}
    </button>
  );
}

/** Separación vertical consistente entre bloques. */
export function Stack({
  children,
  gap = 'var(--gu-space-4)',
}: {
  children: ReactNode;
  gap?: string;
}) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap }}>{children}</div>;
}
